/**
 * Data Import page.
 * CSV file upload with validation, row-level error reporting, and import summary.
 * Route: #/import (admin only)
 * Requirements: 18.1, 18.2, 18.3, 18.4
 */

import { getUser, isAdmin } from '../services/auth.js';
import { importData } from '../services/api.js';
import { navigate } from '../services/router.js';
import { buildCSVTemplate } from '../components/settings/csv-import.js';
import { buildXLSTemplate } from '../components/settings/xls-import.js';

// ---------------------------------------------------------------------------
// Table schema metadata for fields reference and template generation
// ---------------------------------------------------------------------------

export const TABLE_SCHEMAS = {
  flights: {
    columns: [
      { name: 'aircraft_reg', required: true, description: 'Aircraft registration (e.g. G-ABCD)' },
      { name: 'pilot_name', required: true, description: 'Full name of the pilot' },
      { name: 'date', required: true, description: 'Flight date in YYYY-MM-DD format' },
      { name: 'block_time_mins', required: true, description: 'Block time in minutes' },
      { name: 'departure', required: false, description: 'Departure airport/location' },
      { name: 'destination', required: false, description: 'Destination airport/location' },
      { name: 'flight_time_mins', required: false, description: 'Flight time in minutes' },
      { name: 'notes', required: false, description: 'Additional notes' },
    ],
  },
  pilots: {
    columns: [
      { name: 'name', required: true, description: 'Pilot full name' },
      { name: 'email', required: true, description: 'Email address' },
      { name: 'role', required: false, description: 'Role (admin, owner, or pilot)' },
      { name: 'phone', required: false, description: 'Phone number' },
      { name: 'licence_number', required: false, description: 'Licence number' },
      { name: 'licence_expiry', required: false, description: 'Licence expiry date' },
      { name: 'medical_expiry', required: false, description: 'Medical expiry date' },
      { name: 'active', required: false, description: 'Active status (true/false)' },
    ],
  },
  aircraft: {
    columns: [
      { name: 'registration', required: true, description: 'Aircraft registration' },
      { name: 'type', required: true, description: 'Aircraft type (e.g. Cessna 172)' },
      { name: 'name', required: false, description: 'Aircraft name/callsign' },
      { name: 'status', required: false, description: 'Status (active, maintenance, retired)' },
      { name: 'base_airport', required: false, description: 'Base airport ICAO code' },
      { name: 'notes', required: false, description: 'Additional notes' },
    ],
  },
};

// ---------------------------------------------------------------------------
// Pure helper functions (exported for testability)
// ---------------------------------------------------------------------------

/**
 * Parse CSV text into an array of row objects.
 * The first line is treated as the header row.
 *
 * @param {string} csvText - Raw CSV content
 * @returns {Object[]} Array of objects keyed by header columns
 */
export function parseCSVContent(csvText) {
  if (!csvText || typeof csvText !== 'string') return [];

  const lines = csvText.split(/\r?\n/).filter(line => line.trim() !== '');
  if (lines.length < 2) return [];

  const headers = lines[0].split(',').map(h => h.trim());
  const rows = [];

  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',').map(v => v.trim());
    const row = {};
    for (let j = 0; j < headers.length; j++) {
      row[headers[j]] = values[j] !== undefined ? values[j] : '';
    }
    row.__lineNumber = i + 1;
    rows.push(row);
  }

  return rows;
}

/**
 * Validate a single import row for a given table.
 * Returns { valid, errors } where errors lists specific issues.
 *
 * @param {Object} row - A parsed row object
 * @param {string} tableName - Target table name (e.g. 'flights', 'pilots', 'aircraft')
 * @returns {{ valid: boolean, errors: string[] }}
 */
export function validateImportRow(row, tableName) {
  const errors = [];

  if (!row || typeof row !== 'object') {
    return { valid: false, errors: ['Row is empty or not an object'] };
  }

  switch (tableName) {
    case 'flights':
      if (!row.aircraft_reg || !row.aircraft_reg.trim()) errors.push('Missing aircraft_reg');
      if (!row.pilot_name || !row.pilot_name.trim()) errors.push('Missing pilot_name');
      if (!row.date || !row.date.trim()) errors.push('Missing date');
      if (row.date && row.date.trim() && !/^\d{4}-\d{2}-\d{2}$/.test(row.date.trim())) {
        errors.push('Invalid date format (expected YYYY-MM-DD)');
      }
      if (!row.block_time_mins && row.block_time_mins !== '0') {
        errors.push('Missing block_time_mins');
      } else if (isNaN(Number(row.block_time_mins)) || Number(row.block_time_mins) < 0) {
        errors.push('block_time_mins must be a non-negative number');
      }
      break;

    case 'pilots':
      if (!row.name || !row.name.trim()) errors.push('Missing name');
      if (!row.email || !row.email.trim()) errors.push('Missing email');
      if (row.email && row.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email.trim())) {
        errors.push('Invalid email format');
      }
      if (row.role && !['admin', 'owner', 'pilot'].includes(row.role.trim().toLowerCase())) {
        errors.push('Invalid role (must be admin, owner, or pilot)');
      }
      break;

    case 'aircraft':
      if (!row.registration || !row.registration.trim()) errors.push('Missing registration');
      if (!row.type || !row.type.trim()) errors.push('Missing type');
      break;

    default: {
      // Generic: require at least one non-empty field
      const hasData = Object.keys(row)
        .filter(k => k !== '__lineNumber')
        .some(k => row[k] && row[k].trim());
      if (!hasData) errors.push('Row has no data');
      break;
    }
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Separate rows into valid and invalid sets.
 *
 * @param {Object[]} rows - Parsed row objects
 * @param {string} tableName - Target table name
 * @returns {{ validRows: Object[], invalidRows: { row: Object, rowNumber: number, errors: string[] }[] }}
 */
export function processImportData(rows, tableName) {
  const validRows = [];
  const invalidRows = [];

  if (!Array.isArray(rows)) return { validRows, invalidRows };

  for (const row of rows) {
    const result = validateImportRow(row, tableName);
    if (result.valid) {
      // Strip internal metadata before passing to API
      const clean = { ...row };
      delete clean.__lineNumber;
      validRows.push(clean);
    } else {
      invalidRows.push({
        row,
        rowNumber: row.__lineNumber || 0,
        errors: result.errors,
      });
    }
  }

  return { validRows, invalidRows };
}

/**
 * Format a human-readable import summary.
 *
 * @param {number} validCount - Number of successfully imported rows
 * @param {number} invalidCount - Number of skipped invalid rows
 * @param {{ rowNumber: number, errors: string[] }[]} errors - Per-row error details
 * @returns {string} Formatted summary text
 */
export function formatImportSummary(validCount, invalidCount, errors) {
  const parts = [];
  parts.push(`Import complete: ${validCount} record(s) imported successfully.`);

  if (invalidCount > 0) {
    parts.push(`${invalidCount} row(s) skipped due to validation errors.`);
  }

  if (Array.isArray(errors) && errors.length > 0) {
    parts.push('');
    parts.push('Errors:');
    for (const err of errors) {
      parts.push(`  Row ${err.rowNumber}: ${err.errors.join(', ')}`);
    }
  }

  return parts.join('\n');
}

// ---------------------------------------------------------------------------
// Page render
// ---------------------------------------------------------------------------

export async function renderImport() {
  const app = document.getElementById('app');
  const user = getUser();

  if (!user) { navigate('/login'); return; }
  if (!isAdmin()) {
    app.innerHTML = '<div class="container"><div class="card"><p>Admin access required.</p><a href="#/" class="btn">Back</a></div></div>';
    return;
  }


  app.innerHTML = `
    <div class="container">
      <h2>Data Import</h2>
      <div id="import-msg"></div>

      <div class="card">
        <h3>Import Records (CSV)</h3>
        <p style="color:var(--color-text-muted);margin-bottom:1rem;font-size:0.875rem;">
          Upload a CSV file to import records. The first row must be a header row.
          Select the target table and upload your file.
        </p>

        <div class="form-group">
          <label for="import-table">Target Table</label>
          <select id="import-table">
            <option value="flights">Flights</option>
            <option value="pilots">Pilots</option>
            <option value="aircraft">Aircraft</option>
          </select>
        </div>

        <div class="import-fields-panel" id="import-fields-panel"></div>

        <div class="import-template-section" id="import-template-section">
          <div style="flex:1;">
            <label for="import-csv-template" style="font-size:0.875rem;font-weight:600;display:block;margin-bottom:0.25rem;">CSV Template</label>
            <textarea id="import-csv-template" class="import-csv-template" readonly rows="3"></textarea>
          </div>
          <button id="import-xls-download" class="btn btn--secondary" type="button">Download XLS Template</button>
        </div>

        <div class="form-group">
          <label for="import-file">CSV File</label>
          <input type="file" id="import-file" accept=".csv,text/csv" />
        </div>

        <div id="import-validation" class="import-validation-panel" style="display:none;"></div>

        <button id="import-btn" class="btn btn--primary" disabled>Import</button>
      </div>

      <div id="import-report" style="display:none;" class="card"></div>
    </div>
  `;

  // ── Fields reference panel + template helpers ──────────────────────────

  function getFieldsContent(tableName) {
    const schema = TABLE_SCHEMAS[tableName];
    if (!schema) return '';
    return schema.columns.map(col => col.required ? `${col.name}*` : col.name).join(', ');
  }

  function getCSVTemplateContent(tableName) {
    if (tableName === 'flights') {
      const { headerLine, exampleLine } = buildCSVTemplate();
      return headerLine + '\n' + exampleLine;
    }
    const schema = TABLE_SCHEMAS[tableName];
    if (!schema) return '';
    const headers = schema.columns.map(col => col.required ? `${col.name}*` : col.name);
    const example = schema.columns.map(col => col.required ? `example_${col.name}` : '');
    return headers.join(',') + '\n' + example.join(',');
  }

  function updateFieldsPanel(tableName) {
    const panel = document.getElementById('import-fields-panel');
    if (!panel) return;
    const fieldsContent = getFieldsContent(tableName);
    panel.innerHTML = `
      <h4 style="margin-bottom:0.5rem;font-size:0.875rem;">Expected Columns for <strong>${tableName}</strong></h4>
      <textarea readonly rows="2">${fieldsContent}</textarea>
      <p style="font-size:0.8rem;color:var(--color-text-muted);margin-top:0.25rem;">Required columns are marked with *. Copy this header line into your CSV.</p>
    `;
  }

  function updateCSVTemplate(tableName) {
    const textarea = document.getElementById('import-csv-template');
    if (!textarea) return;
    textarea.value = getCSVTemplateContent(tableName);
  }

  function downloadXLSTemplate(tableName) {
    if (tableName === 'flights') {
      const { headers, exampleRow } = buildXLSTemplate();
      const csvContent = headers.join(',') + '\n' + headers.map(h => exampleRow[h] || '').join(',') + '\n';
      downloadAsFile(csvContent, 'flight-import-template.csv', 'text/csv;charset=utf-8;');
      return;
    }
    const schema = TABLE_SCHEMAS[tableName];
    if (!schema) return;
    const headers = schema.columns.map(col => col.name);
    const example = schema.columns.map(col => col.required ? `example_${col.name}` : '');
    const csvContent = headers.join(',') + '\n' + example.join(',') + '\n';
    downloadAsFile(csvContent, `${tableName}-import-template.csv`, 'text/csv;charset=utf-8;');
  }

  function downloadAsFile(content, filename, mimeType) {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  // Initialize fields panel and template for default table
  const tableSelect = document.getElementById('import-table');
  updateFieldsPanel(tableSelect.value);
  updateCSVTemplate(tableSelect.value);

  // Wire table selector change event
  tableSelect.addEventListener('change', () => {
    updateFieldsPanel(tableSelect.value);
    updateCSVTemplate(tableSelect.value);
  });

  // Wire XLS download button
  const xlsDownloadBtn = document.getElementById('import-xls-download');
  xlsDownloadBtn.addEventListener('click', () => {
    downloadXLSTemplate(tableSelect.value);
  });

  const fileInput = document.getElementById('import-file');
  const importBtn = document.getElementById('import-btn');

  fileInput.addEventListener('change', () => {
    const file = fileInput.files[0];
    const validationEl = document.getElementById('import-validation');

    if (!file) {
      importBtn.disabled = true;
      if (validationEl) validationEl.style.display = 'none';
      return;
    }

    // Validate file format before enabling import
    if (!file.name.endsWith('.csv') && file.type !== 'text/csv') {
      showMsg('Invalid file format. Please upload a CSV file.', true);
      importBtn.disabled = true;
      if (validationEl) validationEl.style.display = 'none';
      return;
    }

    showMsg('', false);

    // Keep button disabled until header validation completes
    importBtn.disabled = true;

    // Show validation panel immediately (synchronously) with placeholder
    if (validationEl) {
      validationEl.style.display = 'block';
      validationEl.innerHTML = '<p>Validating CSV headers…</p>';
    }

    // Async: read file content and validate headers against schema
    const selectedTable = tableSelect.value;
    file.text().then((csvText) => {
      const schema = TABLE_SCHEMAS[selectedTable];
      if (!schema) {
        importBtn.disabled = false;
        if (validationEl) validationEl.style.display = 'none';
        return;
      }

      // Extract headers from first line
      const firstLine = csvText.split(/\r?\n/)[0] || '';
      const fileHeaders = firstLine.split(',').map(h => h.trim()).filter(h => h.length > 0);

      const schemaColumns = schema.columns.map(c => c.name);
      const requiredColumns = schema.columns.filter(c => c.required).map(c => c.name);

      // Categorise headers
      const matched = fileHeaders.filter(h => schemaColumns.includes(h));
      const unrecognised = fileHeaders.filter(h => !schemaColumns.includes(h));
      const missingRequired = requiredColumns.filter(r => !fileHeaders.includes(r));

      // Build validation panel HTML
      let html = '<h4 style="margin-bottom:0.5rem;font-size:0.875rem;">Header Validation</h4>';

      if (matched.length > 0) {
        html += `<p class="import-validation__matched">Matched columns: ${matched.join(', ')}</p>`;
      }

      if (missingRequired.length > 0) {
        html += `<p class="import-validation__missing">Required — Missing required columns: ${missingRequired.join(', ')}</p>`;
      }

      if (unrecognised.length > 0) {
        html += `<p class="import-validation__unrecognised">Unrecognised columns: ${unrecognised.join(', ')}</p>`;
      }

      if (missingRequired.length > 0) {
        html += `<p class="import-validation__missing" style="margin-top:0.5rem;">Missing required columns: ${missingRequired.join(', ')}. Please fix your CSV and re-upload.</p>`;
        importBtn.disabled = true;
      } else {
        importBtn.disabled = false;
      }

      if (validationEl) {
        validationEl.innerHTML = html;
      }
    });
  });

  importBtn.addEventListener('click', async () => {
    const file = fileInput.files[0];
    if (!file) return;

    const tableName = document.getElementById('import-table').value;

    // Read file content
    const csvText = await file.text();
    if (!csvText.trim()) {
      showMsg('File is empty.', true);
      return;
    }

    // Parse CSV
    const rows = parseCSVContent(csvText);
    if (rows.length === 0) {
      showMsg('No data rows found in CSV file.', true);
      return;
    }

    // Validate and separate rows
    const { validRows, invalidRows } = processImportData(rows, tableName);

    if (validRows.length === 0) {
      showMsg('No valid rows to import. All rows have validation errors.', true);
      renderReport(0, invalidRows.length, invalidRows);
      return;
    }

    importBtn.disabled = true;
    importBtn.textContent = `Importing ${validRows.length} record(s)…`;

    try {
      await importData(validRows, tableName);
      showMsg(formatImportSummary(validRows.length, invalidRows.length, invalidRows), false);
      renderReport(validRows.length, invalidRows.length, invalidRows);
    } catch (err) {
      showMsg('Import failed: ' + err.message, true);
    } finally {
      importBtn.disabled = false;
      importBtn.textContent = 'Import';
    }
  });
}

function renderReport(validCount, invalidCount, invalidRows) {
  const reportEl = document.getElementById('import-report');
  if (!reportEl) return;
  reportEl.style.display = 'block';

  let html = `
    <h3>Import Summary</h3>
    <table style="width:100%;border-collapse:collapse;">
      <tr><td style="padding:0.25rem 0.5rem;font-weight:600;">Total rows</td><td>${validCount + invalidCount}</td></tr>
      <tr><td style="padding:0.25rem 0.5rem;font-weight:600;">Imported</td><td style="color:var(--color-success);">${validCount}</td></tr>
      <tr><td style="padding:0.25rem 0.5rem;font-weight:600;">Skipped (invalid)</td><td style="color:var(--color-danger);">${invalidCount}</td></tr>
    </table>
  `;

  if (invalidRows.length > 0) {
    html += `
      <h4 style="margin-top:1rem;color:var(--color-danger);">Validation Errors</h4>
      <ul>${invalidRows.map(r =>
        `<li>Row ${r.rowNumber}: ${r.errors.join(', ')}</li>`
      ).join('')}</ul>
    `;
  }

  reportEl.innerHTML = html;
}

function showMsg(text, isError) {
  const el = document.getElementById('import-msg');
  if (!el) return;
  el.textContent = text;
  el.style.whiteSpace = 'pre-wrap';
  el.style.color = isError ? 'var(--color-danger)' : 'var(--color-success)';
  el.style.padding = text ? '0.75rem' : '0';
  el.style.marginBottom = text ? '1rem' : '0';
}
